import { integerAtleastZero, isScheduled, requiredString } from "common";
import { produce } from "immer";
import { isNil } from "lodash";
import {
    AllowedPrivacy,
    Catalog,
    CatalogItem,
    DemographicPricingGroup,
    Experience,
    LineItemTemplate,
    PaymentMethod,
    PricingStructure,
    PricingType,
    Product,
    ProductCatalogItem,
    PurchaseRule,
    PurchaseRuleInitialSequences,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkTag,
    Seller,
    UnitType,
} from "types";
import { array, boolean, mixed, number, object, string } from "yup";
import { getCatalogItemsByUnitType } from "../../../models/Catalog";
import { hasTerms, mergePricingTypeAndTerms } from "../../../models/DemographicPricingGroup";
import { getDefaultGroup } from "../../../models/Experience";
import { isBuyoutPricingPurchaseRule } from "@/models/PricingGroup";

export interface ExperiencePricingFormPayload extends Experience {
    publicPricing: DemographicPricingGroup;
    privatePricing: DemographicPricingGroup;
}

export const getInitialValues = (
    experience: Experience,
    publicPricing: DemographicPricingGroup,
    privatePricing: DemographicPricingGroup,
): ExperiencePricingFormPayload => {
    const defaultGroupValues = getDefaultGroup();
    const isExperienceScheduled = isScheduled(experience);

    const initialValues = produce(experience, (draft) => {
        draft.group = { ...defaultGroupValues, ...experience.group };

        if (!draft.catalog) {
            draft.catalog = { items: [], terms: [], promotions: [] };
        }

        if (!experience.balanceDueOffset) {
            draft.balanceDueOffset = 0;
        }

        if (isNil(experience.autoChargeOnDepositReminder)) {
            draft.autoChargeOnDepositReminder = false;
        }

        if (!experience.paymentMethod) {
            draft.paymentMethod = PaymentMethod.CREDIT_CARD;
        }

        if (!isExperienceScheduled) {
            draft.allowedPrivacies = [AllowedPrivacy.PUBLIC];
        }
    });

    if (!isExperienceScheduled && hasTerms(privatePricing) && !hasTerms(publicPricing)) {
        const unScheduledPricingGroup: DemographicPricingGroup = mergePricingTypeAndTerms(
            publicPricing,
            privatePricing,
        );
        return { ...initialValues, publicPricing: unScheduledPricingGroup, privatePricing };
    }

    return { ...initialValues, publicPricing, privatePricing };
};

export const getPurchaseRuleLinkPayload = (
    seller: Seller,
    product: Product,
    purchaseRuleLink?: PurchaseRuleLink,
    purchaseRules?: PurchaseRule[],
): PurchaseRuleLink => {
    const updatedPurchaseRuleLink: PurchaseRuleLink = purchaseRuleLink?.id
        ? { ...purchaseRuleLink }
        : {
              entities: [{ id: product.id, object: PurchaseRuleLinkEntityObject.EXPERIENCE }],
              purchaseRules: [],
              seller: { id: seller.id },
              tags: [{ id: PurchaseRuleLinkTag.PRICING }],
          };
    const initialSequence = 0;
    updatedPurchaseRuleLink.purchaseRules =
        purchaseRules?.map((purchaseRule, index) => {
            let seq = initialSequence + index;
            if (isBuyoutPricingPurchaseRule(purchaseRule)) {
                seq = PurchaseRuleInitialSequences.BUYOUT_PRICING_SEQUENCE + index;
            }

            return { sequence: seq, purchaseRule: { id: purchaseRule.id! } };
        }) ?? [];

    return updatedPurchaseRuleLink;
};

const getPricingGroupSchema = (
    catalog: Catalog,
    fixedPriceLineItemTemplate: LineItemTemplate,
    supportedCatalogItemTypes: UnitType[] = [],
) => {
    return object().shape({
        privacy: requiredString(),
        pricingStructure: requiredString(),
        pricingType: requiredString(),
        schedules: object().shape({
            all: boolean().required(),
            items: array().of(string()),
        }),
        terms: array()
            .min(1, "should have atleast one tier")
            .test("invalidValue", "- Demographics should not have empty prices", (value, context) => {
                // Added separate Validation for relative/buyout pricing below, so returning true
                if ([PricingStructure.RELATIVE, PricingStructure.BUYOUT].includes(context.parent.pricingStructure)) {
                    return true;
                }

                if (!value || value.length === 0) {
                    return false;
                }

                // Derive equipment-vs-demographic from the selected purchasable item type, not from the
                // catalog: toggling back to Demographic leaves the equipment items in the catalog, and we
                // must validate against the type the seller has actually chosen.
                const isEquipment = supportedCatalogItemTypes.includes(UnitType.EQUIPMENT);

                let hasPrices = true;
                let requiredRowKeys: ID[] = [];
                if (context.parent.pricingType === PricingType.PER_OUTING) {
                    requiredRowKeys = [fixedPriceLineItemTemplate.id];
                } else if (isEquipment) {
                    requiredRowKeys =
                        getCatalogItemsByUnitType(catalog.items, UnitType.EQUIPMENT)
                            .filter((item: CatalogItem) => !item._remove && item.template?.id)
                            .map((item) => item.template!.id) ?? [];
                } else {
                    requiredRowKeys =
                        getCatalogItemsByUnitType(catalog.items, UnitType.DEMOGRAPHIC)
                            .filter((item: CatalogItem) => !item._remove && item.template?.id)
                            .map((item) => item.template!.id) ?? [];
                }

                for (const [index, pricingTerm] of value.entries()) {
                    if (pricingTerm.demographicPrices.length === 0) {
                        hasPrices = false;
                    }

                    const demographicPriceMap: any = {};
                    for (const demographicPrice of pricingTerm.demographicPrices) {
                        demographicPriceMap[demographicPrice.template.id] =
                            index === 0 ? demographicPrice?.price ?? null : demographicPrice?.adjustmentAmount ?? null;
                    }

                    for (const rowKey of requiredRowKeys) {
                        if (isNil(demographicPriceMap[rowKey])) {
                            hasPrices = false;
                            break;
                        }
                    }
                }

                if (!hasPrices) {
                    return context.createError({
                        message: isEquipment
                            ? "- Equipment should not have empty prices"
                            : "- Demographics should not have empty prices",
                    });
                }

                return true;
            })
            .test("invalidAdjustmentAmount", "is required", (value, context) => {
                // Test only for relative pricing, Relative Pricing Term will only have one demographicPrice,
                // which will have only one item in array, and adjustmentAmount is a mandatory field.
                if ([PricingStructure.RELATIVE, PricingStructure.BUYOUT].includes(context.parent.pricingStructure)) {
                    const demographicPrice = value?.[0].demographicPrices;
                    return demographicPrice?.length > 0 && !isNil(demographicPrice[0].adjustmentAmount);
                }

                return true;
            }),
    });
};

export const getValidationSchema = (fixedPriceLineItemTemplate: LineItemTemplate) => {
    return object({
        allowedPrivacies: array().required().min(1, "is required"),
        catalog: object()
            .shape({
                items: array().of(object().shape({})).min(1, "should have atleast one item"),
            })
            .test("equipmentItemsRequired", "should have atleast one item", function (catalog) {
                const supportedCatalogItemTypes = this.parent?.supportedCatalogItemTypes as UnitType[] | undefined;
                if (!supportedCatalogItemTypes?.includes(UnitType.EQUIPMENT)) {
                    return true;
                }

                const items = (catalog?.items ?? []) as unknown as CatalogItem[];
                if (getCatalogItemsByUnitType(items, UnitType.EQUIPMENT).length > 0) {
                    return true;
                }

                return this.createError({ path: `${this.path}.equipmentItems` });
            }),
        balanceDueOffset: integerAtleastZero()
            .required()
            .test("valid number", "should be a valid number", (value) => {
                return Number.isInteger(value ?? 0 / 1440);
            }),
        downDeposit: object()
            .default(null)
            .nullable()
            .shape({
                amount: number().min(0).required(),
                days: integerAtleastZero().required(),
            }),
        publicPricing: object().when(["allowedPrivacies", "catalog", "supportedCatalogItemTypes"], ((
            allowedPrivacies: AllowedPrivacy[],
            catalog: Catalog,
            supportedCatalogItemTypes: UnitType[],
        ) => {
            return allowedPrivacies.includes(AllowedPrivacy.PUBLIC)
                ? getPricingGroupSchema(catalog, fixedPriceLineItemTemplate, supportedCatalogItemTypes)
                : mixed();
        }) as any),
        privatePricing: object().when(["allowedPrivacies", "catalog", "supportedCatalogItemTypes"], ((
            allowedPrivacies: AllowedPrivacy[],
            catalog: Catalog,
            supportedCatalogItemTypes: UnitType[],
        ) => {
            return allowedPrivacies.includes(AllowedPrivacy.PRIVATE)
                ? getPricingGroupSchema(catalog, fixedPriceLineItemTemplate, supportedCatalogItemTypes)
                : mixed();
        }) as any),
        upsellPreference: object().shape({
            enabled: boolean(),
            hidePrivacyToggle: boolean(),
            title: string().when("enabled", {
                is: true,
                then: string().required(),
            }),
            description: string().when("enabled", {
                is: true,
                then: string().required(),
            }),
        }),
    });
};

/**
 * Only Caption can be changed in catalogitem, this function will finds if the caption is changed or not
 * for a catalogItem. This is for making API calls only if there is any change. or a new catalog item
 */
export const shouldSaveCatalogItem = (existingCatalogItems: CatalogItem[], updatedCatalogItem: CatalogItem) => {
    if (!updatedCatalogItem.id) {
        return true;
    }

    const existingItem = existingCatalogItems.find((item) => item.id === updatedCatalogItem.id);

    const captionChanged = existingItem?.caption !== updatedCatalogItem.caption;
    const sequenceChanged = existingItem?.sequence !== updatedCatalogItem.sequence;
    const displayNameChanged =
        (existingItem as ProductCatalogItem)?.displayName !== (updatedCatalogItem as ProductCatalogItem)?.displayName;

    return !existingItem || captionChanged || sequenceChanged || displayNameChanged;
};

export const isLimitAndNotifyGuideHidden = (
    allowedPrivacies: AllowedPrivacy[] = [],
    privatePricing?: DemographicPricingGroup,
) => {
    return (
        allowedPrivacies.length === 1 &&
        allowedPrivacies[0] === AllowedPrivacy.PRIVATE &&
        privatePricing?.pricingType === PricingType.PER_OUTING
    );
};
